import { Component, OnInit, Inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { map, shareReplay, tap, debounceTime } from "rxjs/operators";
import {
    AssessmentService,
    UserService,
    AircraftService,
    USER_IS_AT_LEAST_ADMIN,
} from "@/_services";
import { FilterService } from "@/_services/filter.service";
import { MitigationStats, StatsResult } from "@/_models";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import moment from "moment";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { getTimeFrameList } from "@/_models/filters/default-time-frame-list";
import { FLIGHT_STATUS_LIST } from "@/_models/filters/status-lists";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "app-assessment-statistics",
    templateUrl: "./assessment-statistics.component.html",
    styleUrls: ["./assessment-statistics.component.scss"],
})
export class AssessmentStatisticsComponent implements OnInit {
    stats$: Observable<StatsResult>;
    statsData$: Observable<MitigationStats>;
    assessmentId: string;
    assessmentTitle$: Observable<string>;
    filterForm: UntypedFormGroup;
    multipleStatsData$: Observable<MitigationStats[]>;
    isSingleStatMode = false;
    currentFilters = null;

    statusList = FLIGHT_STATUS_LIST;

    timeFrameList: RadioOption[] = getTimeFrameList(["All"]);

    userList$: Observable<RadioOption[]>;
    aircraftList$: Observable<RadioOption[]>;

    private filterFormSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private assessmentService: AssessmentService,
        private filterService: FilterService,
        private userService: UserService,
        private aircraftService: AircraftService,
        private formBuilder: UntypedFormBuilder,
        @Inject(USER_IS_AT_LEAST_ADMIN) public isAdmin$: Observable<boolean>,
    ) {}

    ngOnInit(): void {
        this.assessmentId = this.route.parent?.snapshot.params["assessmentId"];

        if (this.assessmentId) {
            this.loadStats();
            this.loadAssessmentTitle();
            this.loadFilterData();
        }
    }

    loadStats() {
        const today = moment().format("YYYY-MM-DD");
        const oneYearAgo = moment().subtract(365, "days").format("YYYY-MM-DD");

        const localStorageKey = LOCAL_STORAGE_KEYS.ASSESSMENT_FILTERS(
            this.assessmentId,
        );

        let initialFilters = {
            responsiblePilot: "",
            timeFrame: -365,
            flightDate: [oneYearAgo, today],
            aircraft: "",
            airport: "",
            assessment: "",
            formType: this.assessmentId,
        };

        const savedFilters =
            this.filterService.loadSimpleFilters(localStorageKey);

        if (savedFilters) {
            if (!savedFilters.flightDate || !savedFilters.flightDate.length) {
                savedFilters.flightDate = [oneYearAgo, today];
            }

            if (!savedFilters.timeFrame || savedFilters.timeFrame === "") {
                const [start, end] = savedFilters.flightDate;
                const startDate = moment(start);
                const endDate = moment(end);
                const todayDate = moment(today);
                const diffDays = endDate.diff(startDate, "days");

                if (endDate.isSame(todayDate, "day") && diffDays === 30) {
                    savedFilters.timeFrame = -30;
                } else if (endDate.isSame(todayDate, "day") && diffDays === 7) {
                    savedFilters.timeFrame = -7;
                } else {
                    savedFilters.timeFrame = "custom";
                }
            }

            initialFilters = { ...initialFilters, ...savedFilters };
        }

        this.filterForm = this.formBuilder.group({
            responsiblePilot: [initialFilters.responsiblePilot],
            timeFrame: [initialFilters.timeFrame],
            flightDate: [initialFilters.flightDate],
            aircraft: [initialFilters.aircraft],
            airport: [initialFilters.airport],
            assessment: [initialFilters.assessment],
            formType: [initialFilters.formType],
        });

        this.filterFormSubscription = this.filterForm.valueChanges
            .pipe(
                untilDestroyed(this),
                debounceTime(500),
                tap((changes) => {
                    this.currentFilters = { ...changes };

                    this.filterService.saveSimpleFilters(
                        localStorageKey,
                        changes,
                    );

                    this.updateFlightList();
                }),
            )
            .subscribe();

        this.filterForm.updateValueAndValidity({ emitEvent: true });

        this.currentFilters = { ...this.filterForm.value };

        this.filterService.saveSimpleFilters(
            localStorageKey,
            this.currentFilters,
        );
    }

    updateFlightList(previousPeriod: string = null) {
        try {
            const queryParams = this.filterService.buildFilterQuery(
                this.filterForm,
                previousPeriod,
            );

            this.stats$ = this.userService.getUserStatsBeta(queryParams).pipe(
                tap((stats) => {}),
                shareReplay(),
            );
        } catch (error) {
            console.error("Error in updateFlightList:", error);
        }

        this.statsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? null : stats.data;
                this.isSingleStatMode = !!result;
                return result;
            }),
            shareReplay(),
        );

        this.multipleStatsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? stats.data : null;
                return result;
            }),
            shareReplay(),
        );
    }

    loadAssessmentTitle() {
        this.assessmentTitle$ = this.assessmentService
            .getAssessment(this.assessmentId)
            .pipe(map((assessment) => assessment?.title || "Assessment"));
    }

    private loadFilterData() {
        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => {
                    return { label: user.fullName, value: user._id };
                }),
            ),
            shareReplay(),
        );

        this.aircraftList$ = this.aircraftService.listAircraft().pipe(
            map((aircrafts) =>
                aircrafts.map((aircraft) => {
                    return { label: aircraft.designation, value: aircraft._id };
                }),
            ),
            shareReplay(),
        );
    }
}
